import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { queryClient } from '../../App';

const DisableCategoriesModal = ({
    show = false,
    setShow,
    custom_categories = {},
    default_categories = {},
    checkedItems = [],
    setCheckedItems = () => { },
    mutate,
    restaurant_id
}) => {
    const [tab, setTab] = useState("default");
    const [disabling, setDisabling] = useState(false);

    const categories = tab === 'default' ? default_categories : custom_categories;

    // 🔍 Utility: find path of parents for a node
    function findParentsInData(data, childValue) {
        function search(nodes) {
            for (const node of nodes) {
                if (node.value === childValue) {
                    return [node.value];
                }
                if (node.children) {
                    const childPath = search(node.children);
                    if (childPath) {
                        return [node.value, ...childPath];
                    }
                }
            }
            return null;
        }

        for (const key in data) {
            if (Array.isArray(data[key])) {
                const result = search(data[key]);
                if (result) return result;
            }
        }
        return [];
    }

    // 🔍 Find a node in tree
    function findNode(nodes, targetId) {
        for (const node of nodes) {
            if (node.value === targetId) return node;
            if (Array.isArray(node.children)) {
                const result = findNode(node.children, targetId);
                if (result) return result;
            }
        }
        return null;
    }

    // ✅ Check if ALL children are selected
    function areAllChildrenChecked(node, checkedSet) {
        if (!node.children || node.children.length === 0) return checkedSet.has(node.value);
        return node.children.every(child => areAllChildrenChecked(child, checkedSet));
    }

    const handleCheck = (checked, id) => {
        setCheckedItems(prev => {
            const updated = new Set(prev);
            const allRootNodes = Object.values(categories).flat();

            if (checked) {
                updated.add(id);

                // Walk up and add parent only if ALL children selected
                const path = findParentsInData(categories, id);
                for (let i = path.length - 2; i >= 0; i--) {
                    const parentId = path[i];
                    const parentNode = findNode(allRootNodes, parentId);
                    if (parentNode && areAllChildrenChecked(parentNode, updated)) {
                        updated.add(parentId);
                    } else {
                        break;
                    }
                }

            } else {
                updated.delete(id);

                // Walk up and remove parents since not all children selected anymore
                const path = findParentsInData(categories, id);
                for (let i = path.length - 2; i >= 0; i--) {
                    updated.delete(path[i]);
                }
            }

            return Array.from(updated);
        });
    };

    const renderNode = (node) => {
    const isLeaf = !node.children || node.children.length === 0;
    const isDisabled = !node.active; // inactive category

    return (
        <div key={node.value} style={{ marginLeft: "20px", marginTop: "5px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <input
                    type="checkbox"
                    checked={checkedItems?.includes(node?.value) || isDisabled} // show filled if disabled
                    // disabled={!isLeaf || isDisabled}
                    onChange={(e) => handleCheck(e.target.checked, node.value)}
                    style={{
                        accentColor: isDisabled ? "red" : "blue", // ✅ red for disabled, blue otherwise
                        cursor: isDisabled ? "not-allowed" : "pointer"
                    }}
                />
                <span
                    style={{
                        fontSize: "13px",
                        color: isDisabled ? "var(--bs-secondary-color)" : "var(--bs-body-color)"
                    }}
                >
                    {node.title}
                </span>
            </div>
            {node.children && node.children.length > 0 && (
                <div>
                    {node.children.map(child => renderNode(child))}
                </div>
            )}
        </div>
    );
};

    return (
        <Modal
            show={show}
            onHide={() => setShow(false)}
            backdrop="static"
            keyboard={false}
            centered
            size="md"
        >
            <Modal.Header closeButton>
                <Modal.Title>Disable Categories</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                <button
                    onClick={() => setTab("default")}
                    className={`btn ${tab === "default" ? "btn-success" : "border"} rounded-1 btn-sm`}
                    style={{ fontSize: "12px", paddingTop: "5px", paddingBottom: "5px" }}
                >
                    Default Categories
                </button>
                <button
                    onClick={() => setTab("custom")}
                    className={`btn ${tab === "custom" ? "btn-success" : "border"} rounded-1 btn-sm`}
                    style={{ fontSize: "12px", paddingTop: "5px", paddingBottom: "5px" }}
                >
                    Custom Categories
                </button>
            </div>

                <div className='my-3 border' style={{ display: "flex", flexDirection: "column", maxHeight: "400px", overflowY: "auto" }}>
                    {Object.keys(categories)
                        .filter(topKey => ["menu", "labor", "discount"].includes(topKey)) // ✅ only show these
                        .map((topKey) => (
                            <div key={topKey} style={{ marginBottom: "10px" }}>
                                <span style={{ textTransform: "capitalize", padding: "5px" }}>{topKey}</span>
                                {categories[topKey].map(item => renderNode(item))}
                            </div>
                        ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={() => {
                    setShow(false);
                    setCheckedItems([]);
                    setTab("default");
                }}>Cancel</button>
                <button
                    className="btn btn-success"
                    onClick={() => {
                        setDisabling(true);
                        mutate({ restaurant_id, category_ids: checkedItems }, {
                            onSuccess: () => {
                                setDisabling(false);
                                queryClient.invalidateQueries({ queryKey: ["restaurant-category-detail"] });
                                setCheckedItems([]);
                                setShow(false);
                            },
                            onError: () => {
                                setDisabling(false);
                            }
                        })
                    }}
                >
                    {disabling?"Disabling...":"Disable"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default DisableCategoriesModal;
